import { useEffect, useState, useCallback } from "react";
import supabase from "@/supabase";
import { useSession } from "@/hooks/useSessionHook";

/**
 * Content item from the content_items table
 */
export interface OwnedContentItem {
  id: string;
  type: "image" | "video";
  storage_path: string;
  thumbnail_path: string;
  title: string;
  description_for_user: string;
  credit_cost: number;
  purchased_at: string;
}

/**
 * Bot with its owned content items
 */
export interface BotWithOwnedContent {
  id: string;
  name: string;
  avatar_url: string | null;
  items: OwnedContentItem[];
}

interface UseOwnedContentResult {
  botGroups: BotWithOwnedContent[];
  totalItems: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch all content owned by the current user, grouped by bot
 */
export function useOwnedContent(): UseOwnedContentResult {
  const { session } = useSession();
  const [botGroups, setBotGroups] = useState<BotWithOwnedContent[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOwnedContent = useCallback(async () => {
    if (!session?.user?.id) {
      setBotGroups([]);
      setTotalItems(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch owned content with content item details
      const { data: ownership, error: ownershipError } = await supabase
        .from("content_ownership")
        .select(`
          purchased_at,
          content_items!inner (
            id,
            bot_id,
            type,
            storage_path,
            thumbnail_path,
            title,
            description_for_user,
            credit_cost
          )
        `)
        .eq("user_id", session.user.id)
        .order("purchased_at", { ascending: false });

      if (ownershipError) {
        throw ownershipError;
      }

      if (!ownership || ownership.length === 0) {
        setBotGroups([]);
        setTotalItems(0);
        setIsLoading(false);
        return;
      }

      // Get unique bot IDs from owned content
      const botIds = [...new Set(ownership.map((o) => {
        const item = o.content_items as unknown as { bot_id: string };
        return item.bot_id;
      }))];

      // Fetch bot details
      const { data: bots, error: botsError } = await supabase
        .from("bots")
        .select("id, name, avatar_url")
        .in("id", botIds);

      if (botsError) {
        throw botsError;
      }

      // Create a map of bot ID to bot details
      const botMap = new Map(
        (bots || []).map((bot) => [bot.id, bot])
      );

      // Group content by bot
      const groupedByBot = new Map<string, OwnedContentItem[]>();

      for (const item of ownership) {
        const contentItem = item.content_items as unknown as {
          id: string;
          bot_id: string;
          type: "image" | "video";
          storage_path: string;
          thumbnail_path: string;
          title: string;
          description_for_user: string;
          credit_cost: number;
        };

        const botId = contentItem.bot_id;

        if (!groupedByBot.has(botId)) {
          groupedByBot.set(botId, []);
        }

        groupedByBot.get(botId)!.push({
          id: contentItem.id,
          type: contentItem.type,
          storage_path: contentItem.storage_path,
          thumbnail_path: contentItem.thumbnail_path,
          title: contentItem.title,
          description_for_user: contentItem.description_for_user,
          credit_cost: contentItem.credit_cost,
          purchased_at: item.purchased_at,
        });
      }

      // Convert to array of BotWithOwnedContent
      const groups: BotWithOwnedContent[] = [];

      for (const [botId, items] of groupedByBot) {
        const bot = botMap.get(botId);
        if (bot) {
          groups.push({
            id: bot.id,
            name: bot.name,
            avatar_url: bot.avatar_url,
            items,
          });
        }
      }

      // Sort by most recent purchase across all items in each group
      groups.sort((a, b) => {
        const aLatest = a.items[0]?.purchased_at || "";
        const bLatest = b.items[0]?.purchased_at || "";
        return bLatest.localeCompare(aLatest);
      });

      setBotGroups(groups);
      setTotalItems(ownership.length);
    } catch (err) {
      console.error("Error fetching owned content:", err);
      setError("Failed to load your content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    fetchOwnedContent();
  }, [fetchOwnedContent]);

  return {
    botGroups,
    totalItems,
    isLoading,
    error,
    refetch: fetchOwnedContent,
  };
}
